/* ═══════════════════════════════════════════════════════════════
   PublicSite — the logged-out experience on the web (specforma.app).

   One domain, Procore/SpecLink model: the same site serves the public
   marketing pages (Home / Features / Pricing / About) AND, once you sign
   in, the app itself. Desktop (Electron) skips this and goes straight to
   the LoginPage, so desktop behavior is unchanged.

   No router: the app is state-driven, so this mirrors that with a simple
   `view` state. "Sign in" / "Get started" switch to the existing LoginPage,
   whose onAuth bubbles up to app.tsx and renders the real app.
   ═══════════════════════════════════════════════════════════════ */
import { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { LoginPage } from '../pages/LoginPage';
import type { AuthUser } from '../services/authService';
import type { Go, MarketingView } from './types';
import { MarketingNav } from './MarketingNav';
import { MarketingFooter } from './MarketingFooter';
import { HomePage } from './HomePage';
import { FeaturesPage } from './FeaturesPage';
import { PricingPage } from './PricingPage';
import { AboutPage } from './AboutPage';

interface Props {
  onAuth: (user: AuthUser) => void;
}

export function PublicSite({ onAuth }: Props) {
  const [view, setView] = useState<MarketingView | 'auth'>('home');
  const go: Go = (v) => setView(v);

  // Scroll to top on page change so each "page" starts at the hero.
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, behavior: 'auto' });
    } catch {
      /* non-browser */
    }
  }, [view]);

  if (view === 'auth') {
    return (
      <div className="min-h-dvh bg-bg font-sans text-text">
        <div className="mx-auto flex max-w-6xl items-center px-4 py-4 sm:px-6">
          <button
            type="button"
            onClick={() => go('home')}
            className="inline-flex items-center gap-2 rounded-md px-2 py-2 text-md font-medium text-muted transition-colors hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            Back to site
          </button>
        </div>
        <LoginPage onAuth={onAuth} />
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col bg-bg font-sans text-text">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-inverse"
      >
        Skip to content
      </a>
      <MarketingNav view={view} go={go} />
      <main id="main-content" className="flex-1">
        {view === 'home' && <HomePage go={go} />}
        {view === 'features' && <FeaturesPage go={go} />}
        {view === 'pricing' && <PricingPage />}
        {view === 'about' && <AboutPage go={go} />}
      </main>
      <MarketingFooter go={go} />
    </div>
  );
}
